// Knowledge-base tree (design §10): one collapsible section per space
// (Workspace + projects), folders derived from page paths, the selected
// page highlighted, a stale marker, "new page" via a path input, and
// drag-to-move within a space. Purely presentational: every action is a
// callback, expansion state is local.

import { useEffect, useMemo, useState, type DragEvent, type FormEvent } from 'react'
import {
  KB_AGENT_WRITE_LABEL,
  kbEffectivePolicy,
  validateKbPath,
  type KbFolderPolicy,
  type KbSpace,
  type KbTreeEntry,
  type KbTreeSpace,
} from './kb-types'

export interface KbTreeSelection {
  space: KbSpace
  path: string
}

export interface KbTreeProps {
  spaces: KbTreeSpace[]
  selected?: KbTreeSelection | null
  onSelect: (space: KbSpace, path: string) => void
  /** Shows the "New page" action per space/folder when given. */
  onCreatePage?: (space: KbSpace, path: string) => void
  /**
   * Drag-to-move within one space. For a folder, `from`/`to` are prefixes
   * with trailing slash (e.g. 'areas/' → 'docs/areas/'), as the move route
   * accepts (§6.1).
   */
  onMove?: (space: KbSpace, from: string, to: string) => void
  /** Folder menu action "Agent policy…" (§10). */
  onEditFolderPolicy?: (space: KbSpace, folder: string) => void
  /** Spaces collapsed initially (default: all expanded). */
  defaultCollapsedSpaces?: KbSpace[]
}

interface FolderNode {
  name: string
  /** Prefix with trailing slash; '' for the root. */
  prefix: string
  folders: FolderNode[]
  pages: KbTreeEntry[]
  stale: number
}

function buildTree(entries: KbTreeEntry[]): FolderNode {
  const root: FolderNode = { name: '', prefix: '', folders: [], pages: [], stale: 0 }
  for (const entry of entries) {
    const segments = entry.path.split('/')
    let node = root
    if (entry.stale) node.stale++
    for (const segment of segments.slice(0, -1)) {
      const prefix = `${node.prefix}${segment}/`
      let child = node.folders.find((folder) => folder.prefix === prefix)
      if (!child) {
        child = { name: segment, prefix, folders: [], pages: [], stale: 0 }
        node.folders.push(child)
      }
      node = child
      if (entry.stale) node.stale++
    }
    node.pages.push(entry)
  }
  const sort = (node: FolderNode): void => {
    node.folders.sort((x, y) => x.name.localeCompare(y.name))
    node.pages.sort((x, y) => x.path.localeCompare(y.path))
    node.folders.forEach(sort)
  }
  sort(root)
  return root
}

/** Every folder prefix that contains `path`. */
function ancestors(path: string): string[] {
  const segments = path.split('/').slice(0, -1)
  return segments.map((_, index) => `${segments.slice(0, index + 1).join('/')}/`)
}

const DRAG_TYPE = 'application/x-goblic-kb'

interface DragPayload {
  space: KbSpace
  /** Page path, or folder prefix ending in '/'. */
  from: string
}

function readDrag(event: DragEvent): DragPayload | null {
  try {
    const raw = event.dataTransfer.getData(DRAG_TYPE)
    return raw ? (JSON.parse(raw) as DragPayload) : null
  } catch {
    return null
  }
}

/** Where `from` lands when dropped on folder `target` ('' = root); null when it's a no-op or invalid. */
function moveTarget(from: string, target: string): string | null {
  const isFolder = from.endsWith('/')
  const name = isFolder
    ? from.slice(0, -1).split('/').pop()! + '/'
    : from.split('/').pop()!
  const to = `${target}${name}`
  if (to === from) return null
  // A folder can't move into itself.
  if (isFolder && target.startsWith(from)) return null
  return to
}

export function KbTree({
  spaces,
  selected,
  onSelect,
  onCreatePage,
  onMove,
  onEditFolderPolicy,
  defaultCollapsedSpaces,
}: KbTreeProps) {
  // Expanded keys: `${space}|${prefix}`; spaces use prefix ''.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    for (const space of spaces)
      if (!defaultCollapsedSpaces?.includes(space.space))
        initial.add(`${space.space}|`)
    return initial
  })
  const [creating, setCreating] = useState<{ space: KbSpace; folder: string } | null>(null)
  const [dropKey, setDropKey] = useState<string | null>(null)

  // Reveal the selected page.
  useEffect(() => {
    if (!selected) return
    setExpanded((current) => {
      const next = new Set(current)
      next.add(`${selected.space}|`)
      for (const prefix of ancestors(selected.path))
        next.add(`${selected.space}|${prefix}`)
      return next.size === current.size ? current : next
    })
  }, [selected?.space, selected?.path]) // eslint-disable-line react-hooks/exhaustive-deps

  const trees = useMemo(
    () => spaces.map((space) => ({ space, root: buildTree(space.entries) })),
    [spaces]
  )

  function toggle(key: string): void {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function dropProps(space: KbSpace, target: string) {
    if (!onMove) return {}
    const key = `${space}|${target}`
    return {
      onDragOver: (event: DragEvent) => {
        if (!event.dataTransfer.types.includes(DRAG_TYPE)) return
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
        if (dropKey !== key) setDropKey(key)
      },
      onDragLeave: () => setDropKey((current) => (current === key ? null : current)),
      onDrop: (event: DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        setDropKey(null)
        const payload = readDrag(event)
        if (!payload || payload.space !== space) return
        const to = moveTarget(payload.from, target)
        if (to) onMove(space, payload.from, to)
      },
    }
  }

  function dragProps(space: KbSpace, from: string) {
    if (!onMove) return {}
    return {
      draggable: true,
      onDragStart: (event: DragEvent) => {
        event.stopPropagation()
        event.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ space, from }))
        event.dataTransfer.effectAllowed = 'move'
      },
      onDragEnd: () => setDropKey(null),
    }
  }

  function renderFolder(
    spaceInfo: KbTreeSpace,
    node: FolderNode,
    depth: number
  ) {
    const space = spaceInfo.space
    return (
      <ul role="group">
        {node.folders.map((folder) => {
          const key = `${space}|${folder.prefix}`
          const open = expanded.has(key)
          const policy = spaceInfo.policies?.find((p) => p.folder === folder.prefix)
          return (
            <li key={folder.prefix} role="treeitem" aria-expanded={open}>
              <div
                className={`group flex items-center gap-1 rounded-control pr-1 ${
                  dropKey === key ? 'bg-moss-soft ring-1 ring-moss/40' : 'hover:bg-sunken'
                }`}
                style={{ paddingLeft: depth * 14 }}
                {...dropProps(space, folder.prefix)}
              >
                <button
                  type="button"
                  onClick={() => toggle(key)}
                  className="flex min-w-0 flex-1 items-center gap-1.5 py-1 pl-1 text-left text-base text-ink-2 hover:text-ink"
                  {...dragProps(space, folder.prefix)}
                >
                  <Caret open={open} />
                  <span className="truncate">{folder.name}</span>
                  {policy ? <PolicyMark policy={policy} /> : null}
                  {folder.stale > 0 && !open ? <StaleDot /> : null}
                </button>
                <FolderActions
                  onNew={
                    onCreatePage
                      ? () => {
                          setExpanded((current) => new Set(current).add(key))
                          setCreating({ space, folder: folder.prefix })
                        }
                      : undefined
                  }
                  onPolicy={
                    onEditFolderPolicy
                      ? () => onEditFolderPolicy(space, folder.prefix)
                      : undefined
                  }
                />
              </div>
              {open ? (
                <>
                  {creating?.space === space && creating.folder === folder.prefix ? (
                    <NewPageInput
                      depth={depth + 1}
                      folder={folder.prefix}
                      onCancel={() => setCreating(null)}
                      onCreate={(path) => {
                        setCreating(null)
                        onCreatePage?.(space, path)
                      }}
                    />
                  ) : null}
                  {renderFolder(spaceInfo, folder, depth + 1)}
                </>
              ) : null}
            </li>
          )
        })}
        {node.pages.map((page) => {
          const active = selected?.space === space && selected.path === page.path
          const policy = kbEffectivePolicy(spaceInfo.policies, page.path)
          return (
            <li key={page.path} role="treeitem" aria-selected={active}>
              <button
                type="button"
                onClick={() => onSelect(space, page.path)}
                title={page.summary ?? page.path}
                className={`flex w-full items-center gap-1.5 rounded-control py-1 pr-2 text-left text-base ${
                  active
                    ? 'bg-moss-soft font-medium text-ink'
                    : 'text-ink-2 hover:bg-sunken hover:text-ink'
                }`}
                style={{ paddingLeft: depth * 14 + 4 + 16 }}
                {...dragProps(space, page.path)}
              >
                <span className="truncate">{page.path.split('/').pop()}</span>
                {page.stale ? <StaleDot /> : null}
                {policy?.agentWrite === 'read_only' ? (
                  <span className="sr-only">(read-only for agents)</span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <nav aria-label="Knowledge base" className="space-y-3 text-base">
      {trees.map(({ space: spaceInfo, root }) => {
        const key = `${spaceInfo.space}|`
        const open = expanded.has(key)
        return (
          <div key={spaceInfo.space} role="tree" aria-label={spaceInfo.label}>
            <div
              className={`group flex items-center gap-1 rounded-control pr-1 ${
                dropKey === key ? 'bg-moss-soft ring-1 ring-moss/40' : ''
              }`}
              {...dropProps(spaceInfo.space, '')}
            >
              <button
                type="button"
                onClick={() => toggle(key)}
                aria-expanded={open}
                className="flex min-w-0 flex-1 items-center gap-1.5 py-1 pl-1 text-left font-display text-sm font-semibold uppercase tracking-wide text-ink-3 hover:text-ink"
              >
                <Caret open={open} />
                <span className="truncate">{spaceInfo.label}</span>
                {root.stale > 0 && !open ? <StaleDot /> : null}
              </button>
              <FolderActions
                onNew={
                  onCreatePage
                    ? () => {
                        setExpanded((current) => new Set(current).add(key))
                        setCreating({ space: spaceInfo.space, folder: '' })
                      }
                    : undefined
                }
                onPolicy={
                  onEditFolderPolicy
                    ? () => onEditFolderPolicy(spaceInfo.space, '')
                    : undefined
                }
              />
            </div>
            {open ? (
              <>
                {creating?.space === spaceInfo.space && creating.folder === '' ? (
                  <NewPageInput
                    depth={1}
                    folder=""
                    onCancel={() => setCreating(null)}
                    onCreate={(path) => {
                      setCreating(null)
                      onCreatePage?.(spaceInfo.space, path)
                    }}
                  />
                ) : null}
                {spaceInfo.entries.length === 0 &&
                !(creating?.space === spaceInfo.space) ? (
                  <p className="py-1 pl-6 text-sm text-ink-3">No pages yet.</p>
                ) : (
                  renderFolder(spaceInfo, root, 1)
                )}
              </>
            ) : null}
          </div>
        )
      })}
    </nav>
  )
}

function Caret({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      aria-hidden
      className={`h-3 w-3 shrink-0 text-ink-3 transition-transform ${open ? 'rotate-90' : ''}`}
    >
      <path d="M4 2.5 7.5 6 4 9.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}

function StaleDot() {
  return (
    <span
      className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-signal"
      title="May be out of date"
      aria-label="May be out of date"
    />
  )
}

function PolicyMark({ policy }: { policy: KbFolderPolicy }) {
  if (policy.agentWrite === 'direct') return null
  return (
    <span
      className="shrink-0 rounded-full bg-sunken px-1.5 text-xs text-ink-3"
      title={KB_AGENT_WRITE_LABEL[policy.agentWrite]}
    >
      {policy.agentWrite === 'proposal' ? 'review' : 'read-only'}
    </span>
  )
}

function FolderActions({
  onNew,
  onPolicy,
}: {
  onNew?: () => void
  onPolicy?: () => void
}) {
  if (!onNew && !onPolicy) return null
  return (
    <span className="flex shrink-0 items-center opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
      {onNew ? (
        <button
          type="button"
          onClick={onNew}
          className="rounded px-1 text-sm text-ink-3 hover:bg-surface hover:text-ink"
          title="New page"
          aria-label="New page"
        >
          +
        </button>
      ) : null}
      {onPolicy ? (
        <button
          type="button"
          onClick={onPolicy}
          className="rounded px-1 text-sm text-ink-3 hover:bg-surface hover:text-ink"
          title="Agent policy…"
          aria-label="Agent policy"
        >
          ⋯
        </button>
      ) : null}
    </span>
  )
}

function NewPageInput({
  depth,
  folder,
  onCreate,
  onCancel,
}: {
  depth: number
  folder: string
  onCreate: (path: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = useState(folder)
  const [touched, setTouched] = useState(false)
  const normalized = value.trim().replace(/^\/+/, '')
  const withExt =
    normalized && !normalized.endsWith('.md') && !normalized.endsWith('/')
      ? `${normalized}.md`
      : normalized
  const error = validateKbPath(withExt)

  function submit(event: FormEvent): void {
    event.preventDefault()
    setTouched(true)
    if (!error) onCreate(withExt)
  }

  return (
    <form onSubmit={submit} className="py-1 pr-1" style={{ paddingLeft: depth * 14 + 4 }}>
      <input
        autoFocus
        className="field-input py-1 font-mono text-sm"
        value={value}
        placeholder="folder/page-name.md"
        aria-label="New page path"
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            onCancel()
          }
        }}
        onBlur={() => {
          if (normalized === folder || !normalized) onCancel()
        }}
      />
      {touched && error ? (
        <p className="mt-1 text-xs text-danger">{error}</p>
      ) : (
        <p className="mt-1 text-xs text-ink-3">
          Enter to create · folders follow from the path
        </p>
      )}
    </form>
  )
}
